import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import reject from 'lodash/reject';
import findIndex from 'lodash/findIndex';
import get from 'lodash/get';
import pick from 'lodash/pick';
import Modal from '../../components/Modal';
import Message from '../../components/Message';
import Dropdown from '../../components/Dropdown';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Label from '../../components/Label';
import Icon from '../../components/Icon';
import { ToastKind } from '../../components/Toast';
import AvatarInitial from '../../components/AvatarInitial';
import AppointmentsList from './appointments-list';
import messages from './messages';
import './styles.css';

export const UnExpectedErrorComponent = () => (
  <Message icon warning styleName="unexpectederrorMessage">
    <Icon name="exclamation circle" styleName="unexpectederrorMessageIcon" />
    <Message.Content>
      <Message.Header styleName="unexpectederrorMessageHeading">
        <FormattedMessage id="reassignappointments-unexpectedErrorMessageHeading" defaultMessage="There were some errors." />
      </Message.Header>
      <p styleName="unexpectederrorMessageContent"><FormattedMessage {...messages.unexpectedErrorMessageContent} />
        <a href={`mailto:${process.env.SUPPORT_EMAIL}`}> contact support</a>.
      </p>
    </Message.Content>
  </Message>
);

export default class ReassignAppointments extends React.PureComponent {
  static defaultSelectStaff = 'Select staff member';

  static defaultAssigneeStaff = 'Select new assignee';

  static propTypes = {
    viewer: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onReassignmentSuccess: PropTypes.func.isRequired,
    selectedTab: PropTypes.number,
    reassignRSVP: PropTypes.func.isRequired,
  };

  state = {
    selectedStaffName: ReassignAppointments.defaultSelectStaff,
    newStaffName: ReassignAppointments.defaultAssigneeStaff,
    newStaffUuid: '',
    heUserId: '',
    selected: [],
    noSourceStaffError: false,
    differentSourceStaffError: false,
    targetStaffError: false,
    selectBoxError: false,
    sourceTargetNotUniqueError: false,
    reassignButtonDisabled: false, // prevent double-click on the Reassign button
    userHasAppointments: false,
    error: false,
  };

  onSelected = (event) => {
    const target = get(event, 'target');
    const { id } = target;
    const rsvp = { id,
      dayId: target.getAttribute('data-dayid'),
      appointmentId: target.getAttribute('data-appointmentid'),
      collegeFairId: target.getAttribute('data-collegefairid'),
    };
    const idx = findIndex(this.state.selected, { id });
    if (idx < 0) {
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ selected: [...this.state.selected, rsvp], selectBoxError: false });
    } else {
      this.state.selected.splice(idx, 1);
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ selected: [...this.state.selected], selectBoxError: false });
    }
  };

  onReassign = (event) => {
    const newState = { };
    let isReadyToReassign = true;
    /* istanbul ignore else */
    if (this.state.selectedStaffName === ReassignAppointments.defaultSelectStaff) {
      isReadyToReassign = false;
      newState.noSourceStaffError = true;
    }

    /* istanbul ignore else */
    if (this.state.newStaffName === ReassignAppointments.defaultAssigneeStaff) {
      isReadyToReassign = false;
      newState.targetStaffError = true;
      const targetStaffErrorMessage = document.getElementById('ReassignRsvpModal');
      /* istanbul ignore if */
      if (targetStaffErrorMessage) {
        targetStaffErrorMessage.parentNode.scrollTo(0, 0);
      }
    }

    /* istanbul ignore else */
    if (this.state.selected.length === 0) {
      isReadyToReassign = false;
      newState.selectBoxError = true;
    }

    /* istanbul ignore if */
    if (this.state.userHasAppointments === false) {
      isReadyToReassign = false;
      newState.differentSourceStaffError = true;
    }

    /* istanbul ignore if */
    if (this.state.newStaffName === this.state.selectedStaffName) {
      isReadyToReassign = false;
      newState.sourceTargetNotUniqueError = true;
      const targetStaffErrorMessage = document.getElementById('ReassignRsvpModal');
      targetStaffErrorMessage.parentNode.scrollTo(0, 0);
    }

    newState.reassignButtonDisabled = isReadyToReassign;
    this.setState(newState);
    /* istanbul ignore else */
    if (isReadyToReassign) {
      const onFailure = (err) => {
        const errorMessage = document.getElementById('ReassignRsvpModal');
        this.setState({
          error: true,
          reassignButtonDisabled: false,
          selected: [] });
        errorMessage.scrollIntoView();
      };

      const onSuccess = () => {
        const { selected, newStaffName } = this.state;
        this.props.onReassignmentSuccess(`${selected.length} appointment(s) re-assigned to ${newStaffName}`, ToastKind.success);
        this.onResponse();
      };
      this.props.reassignRSVP({
        variables: {
          input: {
            repVisitEventList: this.state.selected.map((item) => pick(item, ['dayId', 'appointmentId', 'collegeFairId'])),
            fromContactUuid: this.state.heUserId,
            toContactUuid: this.state.newStaffUuid,
          },
        },
      }).then(onSuccess, onFailure);
    }
  };

  onResponse = () => {
    this.setState({ reassignButtonDisabled: false });
    this.closeModal();
  };

  setSourceStaff = (e, { value }) => {
    const newState = {
      selectedStaffName: value.name,
      heUserId: value.uuid,
      noSourceStaffError: false,
      differentSourceStaffError: false,
      sourceTargetNotUniqueError: false,
      selectBoxError: false,
      error: false,
    };
    /* istanbul ignore else */
    if (value.name !== this.state.selectedStaffName) {
      newState.selected = [];
    }
    this.setState(newState);
  };

  setTargetStaff = (e, { value }) => {
    this.setState(
      { newStaffName: value.name, newStaffUuid: value.uuid, targetStaffError: false, sourceTargetNotUniqueError: false, error: false },
    );
  };

  closeModal = () => {
    this.setState({
      selectedStaffName: ReassignAppointments.defaultSelectStaff,
      heUserId: '',
      newStaffName: ReassignAppointments.defaultAssigneeStaff,
      newStaffUuid: '',
      noSourceStaffError: false,
      differentSourceStaffError: false,
      targetStaffError: false,
      sourceTargetNotUniqueError: false,
      selectBoxError: false,
      selected: [],
      userHasAppointments: false,
      error: false,
    });
    this.props.onClose();
  };

  // these rsvps are the actual objects from the graphql query
  selectAllToggle = (rsvps) => {
    if (this.state.selected.length < rsvps.length) {
      const selected = rsvps.map((rsvp) => pick(rsvp, ['id', 'dayId', 'appointmentId', 'collegeFairId']));
      this.setState({ selected, selectBoxError: false });
    } else {
      this.setState({ selected: [] });
    }
  };

  // this is used to toggle error messages on the source user list
  hasAppointments = (rsvpCnt) => {
    this.setState({ userHasAppointments: rsvpCnt > 0 });
  };

  _buildDescription = (user) => {
    let description = !user.isActive ? 'Inactive User' : null;
    description = !description && !user.profileComplete ? 'Profile Incomplete' : description;
    return description;
  };

  // return an array of Dropdown.Item to put into the options, avoids a React warning
  _buildDropdownItems = (userList, onClick, allowDisable) => userList.map((user) => (
    {
      key: user.id,
      text: (
        <span>
          <AvatarInitial user fullName={user.name} size="small" image={user.image} />
          <Dropdown.Header styleName="username">{user.text}</Dropdown.Header>
          <span styleName="dropdowndescription">{user.description}</span>
        </span>
      ),
      disabled: allowDisable && user.shouldDisable,
      value: { name: user.name, uuid: user.uuid },
    }
  ));

  render() {
    const { viewer } = this.props;
    const { selectedStaffName, newStaffName, heUserId, noSourceStaffError,
      differentSourceStaffError, targetStaffError, sourceTargetNotUniqueError, reassignButtonDisabled } = this.state;
    const users = get(viewer, 'heAllCommunityContacts', []);
    const usersList = users.map((user) => ({
      id: user.uuid,
      name: user.name,
      text: `${user.field_last_name}, ${user.field_first_name}`,
      value: `${user.field_last_name}, ${user.field_first_name}`,
      image: '',
      description: this._buildDescription(user),
      shouldDisable: !user.profileComplete || !user.isActive,
      uuid: user.uuid,
    }));

    const sorteduserList = usersList.sort((a, b) => {
      const firstText = a.text.toUpperCase();
      const secondText = b.text.toUpperCase();
      if (firstText < secondText) {
        return -1;
      } else if (firstText > secondText) {
        return 1;
      } else {
        return 0;
      }
    });

    const targetUserList = reject(sorteduserList, ['name', this.state.selectedStaffName]);
    const shouldDisplayResults = selectedStaffName !== ReassignAppointments.defaultSelectStaff;
    return (
      <div>
        <Modal
          id="ReassignRsvpModal"
          open
          size="small"
          onClose={this.closeModal}
          styleName="Reassignmodal"
        >
          <Modal.Header as="h2">
            <FormattedMessage {...messages.heading} />
          </Modal.Header>
          {this.state.error? <UnExpectedErrorComponent />: null}
          <Modal.Content scrolling>
            <div styleName="content" id="content">
              <span styleName="contentsentence">
                <FormattedMessage {...messages.content} />
              </span>
              <div styleName="assigneeDropdown ">
                <Dropdown
                  styleName="staffMember"
                  data-cy="repVisits-calendar-staffMember-dropdown"
                  selection
                  text={selectedStaffName}
                  className="staffSelect"
                  onChange={this.setSourceStaff}
                  selectOnNavigation={false}
                  selectOnBlur={false}
                  options={this._buildDropdownItems(sorteduserList, this.setSourceStaff, false)}
                />
                {(noSourceStaffError || differentSourceStaffError) && (
                <Label role="tooltip" basic styleName="validationError errorLabel" pointing>
                  <Icon name="exclamation circle" styleName="validationError" size="large" />
                  { noSourceStaffError ?
                    <FormattedMessage {...messages.sourceStaffErrorMessage} />:
                    <p styleName="differentStaff"><FormattedMessage {...messages.differentStaffErrorMessage} /></p>}
                </Label>
                )}
              </div>
              <span styleName="contentsentence">
                <FormattedMessage {...messages.contentTo} />
              </span>
              <div styleName="assigneeDropdown">
                <Dropdown
                  styleName="newAssignee"
                  data-cy="repVisits-calendar-newAssignee-dropdown"
                  selection
                  text={newStaffName}
                  className="staffSelect"
                  onChange={this.setTargetStaff}
                  selectOnNavigation={false}
                  selectOnBlur={false}
                  disabled={selectedStaffName === ReassignAppointments.defaultSelectStaff}
                  options={this._buildDropdownItems(targetUserList, this.setTargetStaff, true)}
                />
                {(targetStaffError || sourceTargetNotUniqueError) &&
                  !differentSourceStaffError && (
                  <Label role="tooltip" basic styleName="validationError errorLabel" pointing>
                    <Icon name="exclamation circle" styleName="validationError" size="large" />
                    {
                      targetStaffError ?
                        <FormattedMessage {...messages.targetStaffErrorMessage} /> :
                        <FormattedMessage {...messages.sourceTargetNotUniqueErrorMessage} />
                    }
                  </Label>
                )}
              </div>
            </div>
            {
              this.state.newStaffUuid !=='' &&
              this.state.userHasAppointments && (
              <Message styleName="conflictWarning">
                <Message.Content>
                  <Icon styleName="conflictWarningIcon" name="info circle" size="large" color="grey" />
                  <strong>Note: </strong>
                  <span>
                    <FormattedMessage {...messages.conflictWarningMessage} />
                  </span>
                </Message.Content>
              </Message>
              )
}
            <Modal.Description>
              <Header as="h3" styleName="subheading"><FormattedMessage {...messages.subHeading} /></Header>
              {selectedStaffName === ReassignAppointments.defaultSelectStaff?
                <p styleName="subheadingContent"><FormattedMessage {...messages.subHeadingDescription} /></p>:
                null}
            </Modal.Description>
            {
              shouldDisplayResults && (
              <AppointmentsList
                heUserId={heUserId}
                onSelect={this.onSelected}
                selectAllToggle={this.selectAllToggle}
                selectedAppointments={this.state.selected}
                selectedStaffName={selectedStaffName}
                hasAppointments={this.hasAppointments}
                selectBoxError={this.state.selectBoxError}
              />
              )
}
          </Modal.Content>
          <Modal.Actions>
            <Button
              type="submit"
              styleName="gobackButton"
              content={<FormattedMessage {...messages.backButton} />}
              onClick={this.closeModal}
            />
            <Button
              disabled={reassignButtonDisabled}
              type="submit"
              styleName="reassignAppoinmentsButton"
              data-cy="repVisits-reassignAppointment-button"
              content={`Reassign ${this.state.selected.length < 1 ? '' : this.state.selected.length} Appointments`}
              onClick={this.onReassign}
            />
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}
